// ── imports explicites (Phase 4B, générés puis maintenus à la main) ──


window.__lncAppMounted = true; // drapeau lu par le filet anti-splash-figé ci-dessus (confirme que le script de l'app s'exécute)
const { useState, useEffect, useRef, useCallback } = React;

// ── Phase 5 · AppContext — un contexte UNIQUE, fourni par App(), consommé par les
//   vues extraites (src/views/*.jsx) via useApp(). La valeur est recréée à CHAQUE
//   rendu d'App ⇒ tous les consommateurs re-rendent, EXACTEMENT comme le JSX inline
//   d'avant (comportement identique par construction — zéro régression de rendu).
//   La complétude « chaque symbole lu par une vue est fourni par App » est prouvée
//   à la COMPILATION par tools/view-context-guard.mjs (l'analogue de la garde d'imports).
const AppCtx = React.createContext(null);
const useApp = () => React.useContext(AppCtx);
// Messenger (a) — contexte DÉDIÉ (19/07) : porte les 28 symboles que les 3 montages
//   <Messenger> recevaient à l'identique (anti-prop-drilling). Séparé d'AppCtx car le
//   Messenger n'est pas une « vue » Phase 5 ; valeur recréée à chaque rendu d'App.
const MessengerCtx = React.createContext(null);
const useMessengerCtx = () => React.useContext(MessengerCtx);
const useShell = () => React.useContext(AppCtx).shell;
const useFeed = () => React.useContext(AppCtx).feed;
const useMessaging = () => React.useContext(AppCtx).messaging;
const useDeals = () => React.useContext(AppCtx).deals;
const useAdmin = () => React.useContext(AppCtx).admin;


// ── Journal de debug — silencieux par défaut (console propre en prod) ──────────
//   Les traces de dev `[LANAC] …` sont préfixées par `LANAC_DEBUG && console.…`
//   → quand OFF, l'argument n'est même pas construit (court-circuit = coût ZÉRO).
//   Les console.error / console.warn (vrais problèmes) ne sont JAMAIS touchés.
//   Réactiver pour diagnostiquer :  localStorage.setItem("lanac:debug","1")
const LANAC_DEBUG = (() => { try { return localStorage.getItem("lanac:debug") === "1"; } catch (e) { return false; } })();

// ── Lot B v1 · Routeur par URL — helpers PURS (aucune dépendance React/DOM) ──
//   Source de vérité = l'URL. Chaque écran a son chemin ; le détail est /annonce/:id.
//   pathFor : (view, selId) → chemin ; parsePath : chemin → intention {view|detail}.
//   /messages couvre inbox (liste) ET messages (fil) : la désambiguïsation isMobile
//   se fait à l'application (pas ici), pour garder ces fonctions pures et testables.
const LANAC_VIEW_PATHS = {
  home:"/", market:"/annonces", search:"/recherche", inbox:"/messages",
  messages:"/messages", mine:"/mes-annonces", profile:"/profil",
  favorites:"/favoris", admin:"/admin", nearby:"/a-proximite", map:"/carte", ai:"/assistant",
};
const LANAC_PATH_VIEWS = (() => { // chemin → view (premier gagnant ; /messages → "messages", réglé à l'app)
  const m = {}; for (const v in LANAC_VIEW_PATHS) { const p = LANAC_VIEW_PATHS[v]; if (!(p in m)) m[p] = v; } return m;
})();
const lanacNormPath = (p) => { p = (p || "/").split("?")[0].split("#")[0]; if (p.length > 1 && p.endsWith("/")) p = p.replace(/\/+$/, ""); return p || "/"; };
const lanacPathFor = (view, selId) => (selId != null ? "/annonce/" + encodeURIComponent(String(selId)) : (LANAC_VIEW_PATHS[view] || "/"));
const lanacParsePath = (pathname) => {
  const p = lanacNormPath(pathname);
  const md = p.match(/^\/annonce\/(.+)$/);
  if (md) return { kind: "detail", selId: decodeURIComponent(md[1]) };
  if (p === "/deposer") return { kind: "publish" }; // deep-link « reprendre mon annonce » : ouvre le depot (qui auto-reprend le brouillon)
  if (p in LANAC_PATH_VIEWS) return { kind: "view", view: LANAC_PATH_VIEWS[p] };
  return null; // chemin inconnu (ex. /auth/...) → ne pas router
};

// ── Écran de démarrage « le L atterrit » (10/09/2026) — balisage PARTAGÉ ─────────────────────
//   Le MÊME balisage vit à trois endroits : le splash statique du shell (peint avant tout script),
//   l'écran de boot React (authChecking / splashMinDone) et l'overlay d'atterrissage (SplashLanding,
//   13-ui-kit) — c'est ce qui rend les trois passages sans rupture visuelle (mêmes classes, mêmes
//   keyframes inline). PARITÉ TENUE par tests/splash-markup.test.mjs : LNCB_BOOT_MARKUP (salut vide)
//   doit être octet pour octet le contenu de #root dans src/shell.html.
//   Le salut (« Bonsoir, Mounir. ») vient de window.__lncGreeting — SOURCE UNIQUE dans le shell,
//   0 réseau — et passe par lncbEscape : dangerouslySetInnerHTML ne reçoit JAMAIS un prénom brut.
//   ⭐ 27/09/2026 : on lit le salut FIGÉ de l'ouverture (window.__lncSalut, posé par le shell au premier pixel), jamais
//   un recalcul — l'écran de boot et l'atterrissage disent donc le texte du splash statique, quoi qu'il arrive au
//   prénom ou à l'heure entre-temps. Repli sur la règle si le shell ne l'a pas posé (shell d'avant ce lot).
//   Les replis Suspense (chunks lazy) utilisent LNCB_BOOT_HTML = sans salut (on ne salue pas deux fois).
const LNCB_BOOT_MARKUP = '<div class="lncb-orb lncb-orb1"></div><div class="lncb-orb lncb-orb2"></div><div class="lncb-scene"><div class="lncb-tile"><span class="lncb-Lg">L</span><span class="lncb-sq"></span></div><div class="lncb-word2">Lanac</div><div class="lncb-greet">@GREET@</div></div>';
const lncbEscape = (s) => String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
const lncbGreeting = () => { try { if (typeof window === "undefined") return ""; if (typeof window.__lncSalut === "string") return window.__lncSalut; return typeof window.__lncGreeting === "function" ? String(window.__lncGreeting() || "") : ""; } catch (_) { return ""; } };
const lncbBootHtml = ({ greet = false } = {}) => LNCB_BOOT_MARKUP.replace("@GREET@", greet ? lncbEscape(lncbGreeting()) : "");
//   (10/10/2026) L'écran de PAUSE (hooks/useGarage) est le même écran, avec une ligne à la place du salut — échappée comme lui.
const lncbPauseHtml = (mot) => LNCB_BOOT_MARKUP.replace("@GREET@", lncbEscape(mot || ""));
const LNCB_BOOT_HTML = lncbBootHtml();

// ── Web Push : clé VAPID PUBLIQUE (non secrète) + helper d'encodage ─────────
const LANAC_VAPID_PUBLIC = "BN3yoFemsFp9tu-Fe3T-iLDtD7ElgMLkS6RNH5qdOK8HuEg1hYLudkrPWWqz4JpbhP-3SE3dwet85rgTLVeJUxw";
const urlBase64ToUint8Array = (b64) => {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
};


// ⭐ Audit 23/09/2026 — UN CHUNK PERDU RECHARGE UNE FOIS, IL NE CASSE PAS L'APP. Après un déploiement, un onglet resté
//  ouvert (une PWA reprise après quelques jours) peut demander un chunk lazy que le CDN n'a plus (il répond 200 text/html,
//  règle SPA) : l'import rejette, et la barrière d'erreur remplaçait l'app entière par « Une erreur est survenue ». La
//  navigation, elle, va TOUJOURS au réseau (sw.js) : un rechargement ramène le build courant. Une seule fois par
//  session d'onglet (drapeau sessionStorage, effacé au premier import réussi) — hors ligne, le second échec retombe sur
//  la barrière d'erreur, comme avant : jamais de boucle.
const lncImportOuRecharger=(charger)=>charger().then((m)=>{try{sessionStorage.removeItem("lanac:chunkReload");}catch(e){}return m;},(err)=>{
  try{
    if(typeof window!=="undefined"&&typeof sessionStorage!=="undefined"&&!sessionStorage.getItem("lanac:chunkReload")){
      sessionStorage.setItem("lanac:chunkReload",String(Date.now()));
      window.location.reload();
      return new Promise(()=>{});
    }
  }catch(e){}
  throw err;
});
export { AppCtx, MessengerCtx, useShell, useFeed, useMessaging, useDeals, useAdmin, LANAC_DEBUG, LANAC_PATH_VIEWS, LANAC_VAPID_PUBLIC, LANAC_VIEW_PATHS, LNCB_BOOT_HTML, lncbPauseHtml, lncImportOuRecharger, lncbBootHtml, lanacNormPath, lanacParsePath, lanacPathFor, urlBase64ToUint8Array, useApp, useMessengerCtx, useCallback, useEffect, useRef, useState };
